import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, UserPlus, Upload, Printer, Pencil, Trash2, Loader2, Users, Loader, AlertTriangle, SlidersHorizontal, Lock,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { StudentFormDialog } from "@/components/StudentFormDialog";
import { ImportStudentsDialog } from "@/components/ImportStudentsDialog";
import { CredentialsPrint } from "@/components/CredentialsPrint";
import { EditEvalSystemDialog } from "@/components/EditEvalSystemDialog";
import { TeacherShell } from "@/components/TeacherShell";

const SEX_LABELS = { F: "Fille", M: "Garçon" };
const SCALE_LABELS = { color: "Couleurs", text: "Niveaux textuels", number: "Niveaux chiffrés", custom: "Personnalisé" };


export default function StudentManagement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [className, setClassName] = useState("");
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [printClassOpen, setPrintClassOpen] = useState(false);
  const [printFamilies, setPrintFamilies] = useState(null);
  const [deleteClassOpen, setDeleteClassOpen] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [deletingClass, setDeletingClass] = useState(false);
  const [system, setSystem] = useState(null);
  const [editSysOpen, setEditSysOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [archived, setArchived] = useState(false);

  const load = useCallback(async () => {
    try {
      const [studentsRes, sysRes] = await Promise.all([
        api.get(`/classes/${id}/students`),
        api.get(`/classes/${id}/eval-system`),
      ]);
      setStudents(studentsRes.data.students);
      setClassName(studentsRes.data.class?.name || "");
      setArchived(!!studentsRes.data.class?.archived);
      setSystem(sysRes.data);
    } catch {
      toast.error("Chargement impossible.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (system?.type === "custom") {
      api.get(`/classes/${id}/eval-system/versions`).then((r) => setVersions(r.data.versions)).catch(() => {});
    }
  }, [system, id]);

  const openAdd = () => { setFormMode("add"); setEditing(null); setFormOpen(true); };
  const openEdit = (s) => { setFormMode("edit"); setEditing(s); setFormOpen(true); };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/students/${toDelete.id}`);
      toast.success("Élève supprimé.");
      setToDelete(null);
      load();
    } catch {
      toast.error("Suppression impossible.");
    } finally {
      setDeleting(false);
    }
  };

  const printCredentials = () => setPrintClassOpen(true);

  const deleteClass = null;

  return (
    <TeacherShell active="dashboard" contentClassName="w-full">
      <header className="sticky top-0 z-10 border-b border-slate-100 bg-white/85 backdrop-blur-xl dark:border-white/10 dark:bg-[#0A0A0A]/85 print:hidden">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-3">
          <button
            onClick={() => navigate(`/enseignant/classe/${id}`)}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-transform hover:scale-95 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
            data-testid="back-to-table" aria-label="Retour au tableau"
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">Gestion des élèves</h1>
          {archived && (
            <span className="ml-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-400" data-testid="archived-badge">Classe archivée</span>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-6 print:hidden">
        {archived && (
          <div className="mb-5 flex items-start gap-2 rounded-xl bg-amber-50 p-4 text-sm text-amber-700 dark:bg-amber-500/10 dark:text-amber-400" data-testid="archived-readonly-banner">
            <Lock size={16} className="mt-0.5 shrink-0" />
            Cette classe est archivée : les données sont consultables mais non modifiables. Restaurez-la depuis « Classes archivées » pour la modifier.
          </div>
        )}
        <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {loading ? "…" : `${students.length} élève${students.length > 1 ? "s" : ""}`}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={openAdd} className="rounded-xl" data-testid="add-student-button" disabled={archived}>
              <UserPlus size={17} className="mr-2" /> Ajouter un élève
            </Button>
            <Button variant="outline" onClick={() => setImportOpen(true)} className="rounded-xl" data-testid="import-students-button" disabled={archived}>
              <Upload size={17} className="mr-2" /> Importer une liste
            </Button>
            <Button variant="outline" onClick={printCredentials} className="rounded-xl" data-testid="print-credentials">
              <Printer size={17} className="mr-2" /> Imprimer les identifiants
            </Button>
          </div>
        </div>

        {(() => {
          if (loading) return (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-400" size={26} /></div>
          );
          if (students.length === 0) return (
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-slate-200 py-16 text-center dark:border-white/10" data-testid="empty-students">
            <Users className="text-slate-300" size={36} />
            <p className="text-sm text-slate-500">Aucun élève. Ajoutez-en un ou importez une liste.</p>
          </div>
          );
          return (
          <div className="flex flex-col gap-2" data-testid="students-list">
            {students.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.02 }}
                className="flex items-center justify-between rounded-xl border border-slate-100 bg-white px-4 py-3 dark:border-white/10 dark:bg-white/[0.03]"
                data-testid={`student-item-${s.id}`}
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                    {s.last_name} {s.first_name}
                  </p>
                  <p className="truncate text-xs text-slate-400" data-testid={`student-guardians-${s.id}`}>
                    {[
                      SEX_LABELS[s.sex] || null,
                      ...(s.guardians || []).map((g, gi) => `Resp. ${gi + 1} : ${[g.first_name, g.last_name].filter(Boolean).join(" ") || "—"}${g.identifiant ? ` (${g.identifiant})` : ""}`),
                    ].filter(Boolean).join(" · ") || "—"}
                  </p>
                </div>
                {!archived && (
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(s)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10" data-testid={`edit-student-${s.id}`} aria-label="Modifier">
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => setToDelete(s)} className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600" data-testid={`delete-student-${s.id}`} aria-label="Supprimer">
                    <Trash2 size={16} />
                  </button>
                </div>
                )}
              </motion.div>
            ))}
          </div>
          );
        })()}

        {!loading && system && (
          <section className="mt-8 rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] dark:border-white/10 dark:bg-white/[0.03]" data-testid="eval-system-card">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
              <SlidersHorizontal size={16} className="text-slate-400" /> Système d'évaluation
            </h2>
            {system.type === "passeport" ? (
              <div className="mt-2 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400" data-testid="eval-system-locked">
                <Lock size={14} className="text-slate-400" />
                Système « Le Passeport » (verrouillé) — 3 critères, 6 niveaux couleur. Non modifiable.
              </div>
            ) : (
              <>
                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400" data-testid="eval-system-summary">
                  Système personnalisé — {SCALE_LABELS[system.scale_type] || system.scale_type} · {(system.criteria || []).length} critère{(system.criteria || []).length > 1 ? "s" : ""} · {(system.levels || []).filter((l) => !l.is_none).length} niveaux
                  {system.version_number ? ` · version ${system.version_number}` : ""}
                </p>
                {versions.length > 0 && (
                  <div className="mt-4 border-t border-slate-100 pt-4 dark:border-white/10" data-testid="eval-system-versions">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Historique des versions</p>
                    <div className="flex flex-col gap-1.5">
                      {versions.map((v) => (
                        <div key={v.version_number} className="flex items-start justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2 text-sm dark:bg-white/5" data-testid={`eval-version-${v.version_number}`}>
                          <div className="min-w-0">
                            <span className="font-medium text-slate-700 dark:text-slate-200">Version {v.version_number}</span>
                            {v.is_current && <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-500/20 dark:text-blue-300">actuelle</span>}
                            <p className="truncate text-xs text-slate-400">{SCALE_LABELS[v.scale_type] || v.scale_type} · {v.criteria.length} critère{v.criteria.length > 1 ? "s" : ""} · {v.levels.length} niveaux</p>
                          </div>
                          <span className="shrink-0 text-xs text-slate-400">{v.created_at ? new Date(v.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : ""}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {!archived && (
                <Button
                  onClick={() => setEditSysOpen(true)}
                  variant="outline"
                  className="mt-4 rounded-xl"
                  data-testid="edit-eval-system-button"
                >
                  <Pencil size={16} className="mr-2" /> Modifier le système
                </Button>
                )}
                <EditEvalSystemDialog
                  open={editSysOpen}
                  onOpenChange={setEditSysOpen}
                  classId={id}
                  currentSystem={system}
                  onUpdated={load}
                />
              </>
            )}
          </section>
        )}

      </main>

      {/* Ajout / édition */}
      <StudentFormDialog
        open={formOpen} onOpenChange={setFormOpen} mode={formMode} classId={id}
        student={editing}
        onDone={(cred) => {
          load();
          if (cred) {
            const fam = (cred.credentials || [cred]).map((c) => ({ student_names: [c.student_name], guardian_name: c.guardian_name, identifiant: c.identifiant, temp_password: c.temp_password }));
            setTimeout(() => setPrintFamilies(fam), 150); // laisse le dialogue se fermer (évite l'inertie Radix)
          }
        }}
      />

      {/* Import */}
      <ImportStudentsDialog
        open={importOpen} onOpenChange={setImportOpen} classId={id} onImported={load}
        onRequestPrint={() => { setImportOpen(false); setTimeout(() => setPrintClassOpen(true), 150); }}
      />

      {/* Fiches imprimables (après ajout manuel ou via bouton) */}
      <CredentialsPrint
        open={printClassOpen || !!printFamilies}
        onClose={() => { setPrintClassOpen(false); setPrintFamilies(null); }}
        classId={printFamilies ? undefined : id}
        families={printFamilies || undefined}
        className={className}
      />

      {/* Confirmation de suppression */}
      <Dialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <DialogContent className="rounded-2xl sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Supprimer cet élève ?</DialogTitle>
            <DialogDescription>
              Vous êtes sur le point de supprimer <strong>{toDelete?.first_name} {toDelete?.last_name}</strong>.
              Cette action est <strong>irréversible</strong> : le passeport, l'historique et le compte parent associé seront définitivement supprimés.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setToDelete(null)} data-testid="cancel-delete">Annuler</Button>
            <Button
              className="rounded-xl bg-rose-600 text-white hover:bg-rose-700"
              disabled={deleting} onClick={confirmDelete} data-testid="confirm-delete"
            >
              {deleting ? <Loader size={16} className="animate-spin" /> : "Supprimer définitivement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TeacherShell>
  );
}
